import type {
  IMatchScheduleItem,
  IScheduleConstraints,
  ITournamentSchedule,
} from '../../types/schedule';
import type { Class } from '../../types/tournament';
import { flattenScheduledMatches } from './scheduleOverlap';
import {
  buildPlacementConflictIndex,
  type PlacementConflictIndex,
} from './schedulePlacementConflict';

/** Daily index keys × typical navigation depth across days/classes. */
const MAX_CACHE_ENTRIES = 12;

const cache = new Map<string, PlacementConflictIndex>();

function classFormatsKey(classIdToObject: Record<string, Class>): string {
  return Object.entries(classIdToObject)
    .map(([id, c]) => `${id}:${c.format ?? ''}:${c.moveBox ? 1 : 0}`)
    .sort()
    .join(',');
}

function scheduleMatchFingerprint(schedule: ITournamentSchedule): string {
  const scheduled = flattenScheduledMatches(schedule);
  let maxId = 0;
  let hash = 0;
  for (const m of scheduled) {
    if (m.id > maxId) maxId = m.id;
    hash = (hash * 31 + m.id) | 0;
    const date = m.scheduleDate ?? '';
    for (let i = 0; i < date.length; i++) {
      hash = (hash * 31 + date.charCodeAt(i)) | 0;
    }
    const end = m.scheduleEndDate ?? '';
    for (let i = 0; i < end.length; i++) {
      hash = (hash * 31 + end.charCodeAt(i)) | 0;
    }
  }
  return `${scheduled.length}:${maxId}:${hash}`;
}

export function placementConflictCacheKey(
  schedule: ITournamentSchedule,
  dayKeys: number[] | null,
  unscheduledMatches: IMatchScheduleItem[] = [],
  classIdToObject: Record<string, Class> = {}
): string {
  const structurePart = schedule.days
    .map((d) => `${d.dayKey}:${d.courts.length}`)
    .join('|');
  const matchPart = scheduleMatchFingerprint(schedule);
  const dayPart = dayKeys?.length ? dayKeys.slice().sort((a, b) => a - b).join(',') : 'all';
  const unscheduledPart = unscheduledMatches.length
    ? unscheduledMatches
        .map((m) => `${m.id}:${m.classId}:${m.bracket}:${m.type}`)
        .sort()
        .join(';')
    : '';
  const classPart = classFormatsKey(classIdToObject);
  return `${structurePart}|${matchPart}|${dayPart}|${unscheduledPart}|${classPart}`;
}

export function getPlacementConflictIndex(
  schedule: ITournamentSchedule,
  constraints: IScheduleConstraints,
  timeZone: string,
  dayKeys?: number[] | null,
  unscheduledMatches: IMatchScheduleItem[] = [],
  classIdToObject: Record<string, Class> = {}
): PlacementConflictIndex {
  const key = placementConflictCacheKey(
    schedule,
    dayKeys ?? null,
    unscheduledMatches,
    classIdToObject
  );
  const hit = cache.get(key);
  if (hit) {
    cache.delete(key);
    cache.set(key, hit);
    return hit;
  }

  const index = buildPlacementConflictIndex(
    schedule,
    constraints,
    timeZone,
    [],
    dayKeys,
    unscheduledMatches,
    classIdToObject
  );
  if (cache.size >= MAX_CACHE_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest != null) cache.delete(oldest);
  }
  cache.set(key, index);
  return index;
}

export function clearPlacementConflictIndexCache(): void {
  cache.clear();
}
